import React, {
    useState, useCallback, useRef, useEffect, useMemo,
    useContext,
} from 'react';
import {
    gql, useLazyQuery, useMutation, useQuery, useReactiveVar,
} from '@apollo/client';
import { useRouterState } from 'app/hooks/useRouterState';
import PropTypes from 'prop-types';

import {
    selectedPeriodVar,
    selectedTagsVar,
    checkoutDeliveryDateVar,
    isBasketFilledVar,
} from 'app/apollo/reaction';
import { pickPeriodReaction } from 'app/apollo/reactions/filters';

import { FETCH_DELIVERY_ADDRESS_SESSION } from 'app/graphql/network/deliveryAddress.query';
import FILTER_QUERY from 'app/graphql/network/filter';
import BASKET_QUERY from 'app/graphql/network/basket/basketQuery';
import { ADD_ITEMS_TO_CART_BY_PERIOD } from 'app/graphql/network/basket/basketMutations';

import { InactivePeriodDialogContainer } from 'app/components/Dialog';
import { FiltersContext } from 'app/containers/contexts/filters.context/filters.context';
import { useBasketData } from 'app/hooks/accessors';

const isSkipBasket = (props) => {
    const periodIsNotSelected = props.selectedFilters.selectedPeriod === '';

    return (
        periodIsNotSelected
        || !props.isBasketFilled
        || props.match.params.deliveryId
        || props.history.location.pathname.includes('/pay')
    );
};

const BASKET_PAGE_ROOT_QUERY_COPY = gql`
    query BASKET_PAGE_ROOT_QUERY_COPY(
    $period: String!
) {
    selectedPeriod @client @export(as: "period")

    cart(
        period: $period
    ) {
        period
        isSubscriptionActivated
        id
        typeOfSet
        isSectionsEmpty @client
        type
        sections {
            id
            title
            gift_category
            cartType
            items {
                ...ROOT_CART_DISH_ITEM
                dish {
                    ...ROOT_MENU_DISH_ITEM
                }
            }
        }
        totals {
            ...ROOT_CART_TOTAL
        }
        discount_conditions {
            ...ROOT_CART_DISCOUNT_CONDITIONS
        }
    }
}`;
export const useBasketQueryAdapter = () => {
    const selectedFilters = useContext(FiltersContext);

    const [routerState] = useRouterState();
    const { history, match } = routerState;

    const [showInactivePeriodDialog, setShowInactivePeriodDialog] = useState(false);
    const basketDataRef = useRef(null);

    const selectedPeriod = useReactiveVar(selectedPeriodVar);
    const checkoutDeliveryDate = useReactiveVar(checkoutDeliveryDateVar);
    const isBasketFilled = useReactiveVar(isBasketFilledVar);

    /* DA */
    const { data: filtersData } = useQuery(FILTER_QUERY, {
        context: {
            message: '&:BasketQueryAdapter',
        },
    });
    const data = useBasketData();


    const [_, { refetch }] = useLazyQuery(BASKET_QUERY,
        {
            variables:
            {
                period: selectedPeriod,
                clear: ({ clear }) => clear,
            },
        });

    return {
        basketQuery: { cart: data.cart, refetch },
        showInactivePeriodDialog,
        setShowInactivePeriodDialog,
    };
};

export function BasketQueryAdapter({
    children,
}) {
    const { basketQuery, showInactivePeriodDialog, setShowInactivePeriodDialog } = useBasketQueryAdapter();

    const selectedPeriod = useReactiveVar(selectedPeriodVar);
    const selectedTags = useReactiveVar(selectedTagsVar);
    const selectedPeriodPastValue = useRef(null);

    /* DA */
    const [addItemsToCartByPeriod] = useMutation(ADD_ITEMS_TO_CART_BY_PERIOD);
    const { data: deliveryAddressData } = useQuery(FETCH_DELIVERY_ADDRESS_SESSION);
    const { data: filtersData } = useQuery(FILTER_QUERY, {
        context: {
            message: '&:BasketQueryAdapter',
        },
    });

    /* HANDLERS  */
    const handleInactivePeriodDialogConfirmBasketFilling = useCallback(async () => {
        const { menuFilter: { periods } } = filtersData;

        setShowInactivePeriodDialog(false);

        const currentPeriodIndex = periods.findIndex((p) => p.start === selectedPeriod);
        const nextPeriod = periods[currentPeriodIndex + 1];

        if (!nextPeriod) {
            window.location = '/menu/';
            return;
        }

        pickPeriodReaction({ periodStart: nextPeriod.start });

        await addItemsToCartByPeriod({
            variables: {
                period: nextPeriod.start,
                fillByPeriod: selectedPeriod,
                tags: selectedTags,
                source_name: window.location.pathname?.replace(/^\/+|\/+$/g, ''),
            },
        });

        window.location.reload();
    }, [addItemsToCartByPeriod, filtersData, selectedPeriod, selectedTags]);

    /* SELECTROS  */
    const [latitude, longitude] = useMemo(() => {
        if (deliveryAddressData) {
            const { deliveryAddress: { address: { coordinates } } } = deliveryAddressData;
            if (!coordinates) {
                return [null, null];
            }
            return [coordinates.latitude, coordinates.longitude];
        }
        return [null, null];
    }, [deliveryAddressData]);

    /* SELECTROS  */
    useEffect(() => {
        const hasCoordinates = Boolean(latitude && longitude);
        const hasVariables = Boolean(selectedPeriod);
        const isRefetchNeeded = hasCoordinates && hasVariables;

        if (isRefetchNeeded) {
            basketQuery.refetch({ clear: false });
        }

        selectedPeriodPastValue.current = selectedPeriod;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latitude, longitude, selectedPeriod]);

    return (
        <>
            {children({
                basketQuery,
            })}
            <InactivePeriodDialogContainer
                isDialogRequired={showInactivePeriodDialog}
                onConfirm={() => alert('ok')}
            />
        </>
    );
}

BasketQueryAdapter.propTypes = {
    selectedFilters: PropTypes.shape({
        selectedPeriodVar: PropTypes.string,
    }).isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            deliveryId: PropTypes.string,
        }),
    }).isRequired,
    history: PropTypes.shape({}).isRequired,
    children: PropTypes.func.isRequired,
};

export function withBasketQuery(Child) {
    return (props) => (
        <BasketQueryAdapter {...props}>
            {({ basketQuery }) => (
                <Child {...props} basketQuery={basketQuery} />
            )}
        </BasketQueryAdapter>
    );
}
